import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams, useOutletContext } from 'react-router-dom';
import crmApi, { money, CURRENCIES } from '../../utils/crmApi';

const round3 = n => Math.round((n + Number.EPSILON) * 1000) / 1000;

// Shared editor for quote / sales_order / invoice.
export default function CrmDocumentEditor({ docType, routeBase, title }) {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useOutletContext();
  const editing = id && id !== 'new';

  const [accounts, setAccounts] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const [doc, setDoc] = useState({
    account_id: sp.get('account') || '',
    deal_id: sp.get('deal') || '',
    contact_id: '',
    salesperson_id: user?.id || '',
    currency: 'OMR',
    discount_type: 'none',
    discount_value: 0,
    tax_rate: 0,
    notes: '',
    terms: '',
    issue_date: new Date().toISOString().slice(0, 10),
    due_date: '',
    recognition_month: '',
    service_start_month: '',
    service_end_month: '',
    items: [],
  });

  useEffect(() => {
    Promise.all([
      crmApi.get('/accounts'),
      crmApi.get('/products'),
      crmApi.get('/users').then(r => r).catch(() => []),
    ]).then(([a, p, u]) => {
      setAccounts(a); setProducts(p); setUsers(Array.isArray(u) ? u : []);
      if (editing) {
        crmApi.get(`/documents/${id}`).then(d => {
          setDoc({
            account_id: d.account_id || '', deal_id: d.deal_id || '', contact_id: d.contact_id || '',
            salesperson_id: d.salesperson_id || '', currency: d.currency, discount_type: d.discount_type,
            discount_value: Number(d.discount_value), tax_rate: Number(d.tax_rate), notes: d.notes || '',
            terms: d.terms || '', issue_date: d.issue_date || '', due_date: d.due_date || '',
            recognition_month: d.recognition_month ? String(d.recognition_month).slice(0, 7) : '',
            service_start_month: d.service_start_month ? String(d.service_start_month).slice(0, 7) : '',
            service_end_month: d.service_end_month ? String(d.service_end_month).slice(0, 7) : '',
            items: d.items.map(it => ({ product_id: it.product_id || '', name: it.name, description: it.description || '', unit: it.unit, quantity: Number(it.quantity), unit_price: Number(it.unit_price), currency: it.line_currency || d.currency || 'OMR', cost_price: Number(it.cost_price) || 0, billing_type: it.billing_type || 'one_time', discount_type: it.discount_type || 'none', discount_value: Number(it.discount_value) || 0, show_discount: it.show_discount === 0 ? false : true, fx_rate: it.fx_rate != null ? Number(it.fx_rate) : 1 })),
          });
          setLoaded(true);
        });
      } else { setLoaded(true); }
    });
  }, [id]);

  // Per-line net after its own adjustment (discount OR increase)
  function effUnit(it) {
    const base = Number(it.unit_price) || 0; const val = Number(it.discount_value) || 0;
    if (it.discount_type === 'amount') return round3(Math.max(0, base - Math.min(val, base)));
    if (it.discount_type === 'percent') return round3(base * (1 - Math.min(val, 100) / 100));
    if (it.discount_type === 'inc_amount') return round3(base + Math.max(0, val));
    if (it.discount_type === 'inc_percent') return round3(base * (1 + Math.max(0, val) / 100));
    return base;
  }
  function lineNet(it) {
    return round3((Number(it.quantity) || 0) * effUnit(it));
  }

  // ── Totals grouped by currency (items may have different currencies) ──────
  const totals = useMemo(() => {
    const groups = {};
    doc.items.forEach(it => {
      const cur = it.currency || doc.currency || 'OMR';
      groups[cur] = (groups[cur] || 0) + lineNet(it);
    });
    // Discount + tax apply to each currency group proportionally to its subtotal.
    const result = Object.keys(groups).map(cur => {
      const subtotal = round3(groups[cur]);
      let discountAmount = 0;
      if (doc.discount_type === 'amount') discountAmount = round3(Math.min(Number(doc.discount_value) || 0, subtotal));
      else if (doc.discount_type === 'percent') discountAmount = round3(subtotal * Math.min(Number(doc.discount_value) || 0, 100) / 100);
      const afterDiscount = round3(subtotal - discountAmount);
      const taxAmount = round3(afterDiscount * (Number(doc.tax_rate) || 0) / 100);
      const total = round3(afterDiscount + taxAmount);
      return { currency: cur, subtotal, discountAmount, taxAmount, total };
    });
    return result.length ? result : [{ currency: doc.currency || 'OMR', subtotal: 0, discountAmount: 0, taxAmount: 0, total: 0 }];
  }, [doc.items, doc.discount_type, doc.discount_value, doc.tax_rate, doc.currency]);

  function addItem() { setDoc(d => ({ ...d, items: [...d.items, { product_id: '', name: '', description: '', unit: 'unit', quantity: 1, unit_price: 0, currency: d.currency || 'OMR', cost_price: 0, billing_type: 'one_time', discount_type: 'none', discount_value: 0, show_discount: true, fx_rate: 1 }] })); }
  function removeItem(i) { setDoc(d => ({ ...d, items: d.items.filter((_, idx) => idx !== i) })); }
  function setItem(i, patch) { setDoc(d => ({ ...d, items: d.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) })); }
  function pickProduct(i, productId) {
    const p = products.find(x => x.id === productId);
    if (p) setItem(i, { product_id: p.id, name: p.name, unit: p.unit, unit_price: Number(p.unit_price), currency: p.currency || doc.currency || 'OMR', cost_price: Number(p.cost_price) || 0, billing_type: p.billing_type || 'one_time' });
    else setItem(i, { product_id: '' });
  }

  async function save(goBack = true) {
    const payload = { ...doc, doc_type: docType };
    if (editing) { await crmApi.put(`/documents/${id}`, payload); if (goBack) navigate(`/crm/${routeBase}/${id}`); }
    else { const r = await crmApi.post('/documents', payload); navigate(`/crm/${routeBase}/${r.id}`); }
  }

  if (!loaded) return <div style={{ color: 'var(--muted)' }}>Loading…</div>;

  return (
    <div style={{ maxWidth: '1100px' }}>
      <button className="btn" style={{ marginBottom: '14px' }} onClick={() => navigate(`/crm/${routeBase}`)}>← {title}</button>
      <h1 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '16px' }}>{editing ? `Edit ${title.replace(/s$/, '')}` : `New ${title.replace(/s$/, '')}`}</h1>

      <div className="card" style={{ padding: '18px', marginBottom: '14px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
          <div className="form-group"><label className="form-label">Account</label>
            <select className="select" value={doc.account_id} onChange={e => setDoc({ ...doc, account_id: e.target.value })}>
              <option value="">— None —</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div className="form-group"><label className="form-label">Salesperson <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 11 }}>(approver)</span></label>
            <select className="select" value={doc.salesperson_id} onChange={e => setDoc({ ...doc, salesperson_id: e.target.value })}>
              <option value="">— None —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          </div>
          <div className="form-group"><label className="form-label">Default Currency</label>
            <select className="select" value={doc.currency} onChange={e => setDoc({ ...doc, currency: e.target.value })}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-group"><label className="form-label">Issue Date</label><input className="input" type="date" value={doc.issue_date} onChange={e => setDoc({ ...doc, issue_date: e.target.value })} /></div>
          <div className="form-group"><label className="form-label">Due Date</label><input className="input" type="date" value={doc.due_date} onChange={e => setDoc({ ...doc, due_date: e.target.value })} /></div>
          <div className="form-group"><label className="form-label">Revenue Month <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>(one-time recognition)</span></label><input className="input" type="month" value={doc.recognition_month} onChange={e => setDoc({ ...doc, recognition_month: e.target.value })} /></div>
          <div className="form-group"><label className="form-label">Service Start <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>(recurring begins)</span></label><input className="input" type="month" value={doc.service_start_month} onChange={e => setDoc({ ...doc, service_start_month: e.target.value })} /></div>
          <div className="form-group"><label className="form-label">Service End <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>(capacity returns to stock)</span></label><input className="input" type="month" value={doc.service_end_month} onChange={e => setDoc({ ...doc, service_end_month: e.target.value })} /></div>
        </div>
      </div>

      {/* Line items */}
      <div className="card" style={{ padding: '18px', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
          <div style={{ fontSize: '14px', fontWeight: '800' }}>Line Items</div>
          <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={addItem}>+ Add Item</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.1fr 0.6fr 0.9fr 0.7fr 1.5fr 1fr 30px', gap: '7px', fontSize: '10px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', padding: '0 2px 6px' }}>
          <div>Item</div><div>Product</div><div>Qty</div><div>Unit Price</div><div>Curr.</div><div>Adjust</div><div>Line Total</div><div></div>
        </div>
        {doc.items.length === 0 && <div style={{ fontSize: '12px', color: 'var(--muted)', padding: '8px 0' }}>No items yet. Click "Add Item".</div>}
        {doc.items.map((it, i) => {
          const cur = it.currency || doc.currency;
          const isDiscount = it.discount_type === 'amount' || it.discount_type === 'percent';
          const needsFx = cur && cur !== 'OMR';
          return (
          <div key={i} style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.1fr 0.6fr 0.9fr 0.7fr 1.5fr 1fr 30px', gap: '7px', alignItems: 'center' }}>
              <input className="input" value={it.name} onChange={e => setItem(i, { name: e.target.value })} placeholder="Description" />
              <select className="select" value={it.product_id} onChange={e => pickProduct(i, e.target.value)}>
                <option value="">Custom</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <input className="input" type="number" value={it.quantity} onChange={e => setItem(i, { quantity: e.target.value })} />
              <input className="input" type="number" value={it.unit_price} onChange={e => setItem(i, { unit_price: e.target.value })} />
              <select className="select" value={cur} onChange={e => setItem(i, { currency: e.target.value })}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <div style={{ display: 'flex', gap: '4px' }}>
                <select className="select" style={{ padding: '8px 4px', fontSize: '11px', flex: 1 }} value={it.discount_type || 'none'} onChange={e => setItem(i, { discount_type: e.target.value })} title="Per-line price adjustment">
                  <option value="none">—</option>
                  <option value="amount">− Amt</option>
                  <option value="percent">− %</option>
                  <option value="inc_amount">+ Amt</option>
                  <option value="inc_percent">+ %</option>
                </select>
                {it.discount_type && it.discount_type !== 'none' && (
                  <input className="input" style={{ width: '64px' }} type="number" value={it.discount_value} onChange={e => setItem(i, { discount_value: e.target.value })} />
                )}
              </div>
              <div style={{ fontSize: '12px', fontWeight: '700', padding: '8px 4px' }}>{cur} {lineNet(it).toFixed(3)}</div>
              <button onClick={() => removeItem(i)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '5px', cursor: 'pointer', color: '#DC2626', height: '34px' }}>✕</button>
            </div>
            {/* options subrow */}
            {(isDiscount || needsFx) && (
              <div style={{ display: 'flex', gap: '18px', alignItems: 'center', marginTop: '7px', paddingLeft: '2px', flexWrap: 'wrap' }}>
                {isDiscount && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--muted2)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={it.show_discount !== false && it.show_discount !== 0} onChange={e => setItem(i, { show_discount: e.target.checked })} />
                    Show this discount on the customer document
                  </label>
                )}
                {needsFx && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--muted2)' }}>
                    1 {cur} =
                    <input className="input" style={{ width: '90px' }} type="number" step="0.001" value={it.fx_rate ?? ''} placeholder="rate"
                      onChange={e => setItem(i, { fx_rate: e.target.value })} />
                    OMR <span style={{ color: 'var(--muted)' }}>(for forecast &amp; reporting)</span>
                  </label>
                )}
              </div>
            )}
          </div>
          );
        })}
      </div>

      {/* Discounts + tax + totals */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
        <div className="card" style={{ padding: '18px' }}>
          <div style={{ fontSize: '14px', fontWeight: '800', marginBottom: '12px' }}>Discount & Tax</div>
          <div className="form-group"><label className="form-label">Discount Type</label>
            <select className="select" value={doc.discount_type} onChange={e => setDoc({ ...doc, discount_type: e.target.value })}>
              <option value="none">No discount</option>
              <option value="amount">Fixed amount</option>
              <option value="percent">Percentage (%)</option>
            </select>
          </div>
          {doc.discount_type !== 'none' && (
            <div className="form-group">
              <label className="form-label">Discount {doc.discount_type === 'percent' ? '(%)' : '(amount, applied per currency)'}</label>
              <input className="input" type="number" value={doc.discount_value} onChange={e => setDoc({ ...doc, discount_value: e.target.value })} />
            </div>
          )}
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Tax / VAT Rate (%)</label><input className="input" type="number" value={doc.tax_rate} onChange={e => setDoc({ ...doc, tax_rate: e.target.value })} /></div>
        </div>

        <div className="card" style={{ padding: '18px' }}>
          <div style={{ fontSize: '14px', fontWeight: '800', marginBottom: '12px' }}>Summary</div>
          {totals.length > 1 && <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '10px' }}>Items use more than one currency — totals are shown per currency.</div>}
          {totals.map((t, idx) => (
            <div key={t.currency} style={{ marginBottom: idx < totals.length - 1 ? '14px' : 0, paddingBottom: idx < totals.length - 1 ? '14px' : 0, borderBottom: idx < totals.length - 1 ? '1px dashed var(--border)' : 'none' }}>
              {totals.length > 1 && <div style={{ fontSize: '11px', fontWeight: '700', color: '#0D9488', marginBottom: '4px' }}>{t.currency}</div>}
              <Row label="Subtotal" value={money(t.subtotal, t.currency)} />
              {t.discountAmount > 0 && <Row label={`Discount${doc.discount_type === 'percent' ? ` (${doc.discount_value}%)` : ''}`} value={`− ${money(t.discountAmount, t.currency)}`} color="#DC2626" />}
              {t.taxAmount > 0 && <Row label={`Tax (${doc.tax_rate}%)`} value={money(t.taxAmount, t.currency)} />}
              <div style={{ borderTop: '2px solid var(--border)', marginTop: '8px', paddingTop: '8px' }}>
                <Row label="Total" value={money(t.total, t.currency)} bold />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: '18px', marginBottom: '14px' }}>
        <div className="form-group"><label className="form-label">Notes</label><textarea className="input" rows={2} value={doc.notes} onChange={e => setDoc({ ...doc, notes: e.target.value })} /></div>
        <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Terms</label><textarea className="input" rows={2} value={doc.terms} onChange={e => setDoc({ ...doc, terms: e.target.value })} placeholder="e.g. Payment due within 30 days" /></div>
      </div>

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button className="btn" onClick={() => navigate(`/crm/${routeBase}`)}>Cancel</button>
        <button className="btn btn-primary" onClick={() => save(true)}>{editing ? 'Save Changes' : 'Create ' + title.replace(/s$/, '')}</button>
      </div>
    </div>
  );
}

function Row({ label, value, bold, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: bold ? '16px' : '13px', fontWeight: bold ? '800' : '500' }}>
      <span style={{ color: 'var(--muted2)' }}>{label}</span>
      <span style={{ color: color || 'var(--text)' }}>{value}</span>
    </div>
  );
}

export const QuoteEditor = () => <CrmDocumentEditor docType="quote" routeBase="quotes" title="Quotes" />;
export const SalesOrderEditor = () => <CrmDocumentEditor docType="sales_order" routeBase="sales-orders" title="Service Orders" />;
export const InvoiceEditor = () => <CrmDocumentEditor docType="invoice" routeBase="invoices" title="Invoices" />;
